
import React from 'react';
import { CarIssue } from '@/contexts/CarContext';
import { useI18n } from '@/i18n/LanguageContext';

interface StatusWidgetProps {
  carId?: string;
  issues: CarIssue[];
  compact?: boolean; // when true, render single-line without description
}

const StatusWidget: React.FC<StatusWidgetProps> = ({ carId, issues, compact = false }) => {
  const { t } = useI18n();
  const openIssues = issues?.filter(issue => issue.status !== 'resolved') || [];
  
  const getStatusInfo = () => {
    if (!openIssues.length) {
      return { 
        icon: '✅', 
        message: t('cars.statusAllGood'), 
        description: t('cars.statusNoOpenIssues'),
        color: 'text-lime'
      };
    }
    
    const urgentIssues = openIssues.filter(issue => 
      issue.aiSummary?.includes('urgent') || 
      issue.title?.toLowerCase().includes('urgent') ||
      issue.description?.toLowerCase().includes('urgent')
    );
    
    if (urgentIssues.length > 0) {
      const key = urgentIssues.length === 1 ? 'cars.statusUrgentIssue' : 'cars.statusUrgentIssues';
      return { 
        icon: '🚨', 
        message: t(key, { count: urgentIssues.length }), 
        description: t('cars.statusNeedsAttention'),
        color: 'text-red-500'
      };
    }
    
    const recurringIssues = openIssues.filter(issue => {
      const count = openIssues.filter(i => i.title === issue.title).length;
      return count > 1;
    });
    
    if (recurringIssues.length > 0) {
      const uniqueRecurringTitles = new Set(recurringIssues.map(issue => issue.title));
      const count = uniqueRecurringTitles.size;
      const key = count === 1 ? 'cars.statusRecurringIssue' : 'cars.statusRecurringIssues';
      return { 
        icon: '🔁', 
        message: t(key, { count }), 
        description: Array.from(uniqueRecurringTitles)[0],
        color: 'text-yellow-500'
      };
    }
    
    const count = openIssues.length;
    const key = count === 1 ? 'cars.statusOpenIssue' : 'cars.statusOpenIssues';
    return { 
      icon: '⚠️', 
      message: t(key, { count }), 
      description: openIssues[0].title,
      color: 'text-yellow-500'
    };
  };
  
  const status = getStatusInfo();
  const hasOpenIssues = (openIssues?.length || 0) > 0;
  const statusIconSrc = hasOpenIssues ? '/icons/Error.png' : '/icons/Good.png';
  
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <img src={statusIconSrc} alt="status" className="w-6 h-6" />
        <span className="text-sm text-gray-500 font-medium">{status.message}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center w-full">
      <img src={statusIconSrc} alt="status" className="w-6 h-6 mr-2" />
      <div className="flex flex-col">
        <span className="text-sm font-medium">{status.message}</span>
        {status.description && (
          <p className="text-xs text-gray-500 line-clamp-1">
            {status.description}
          </p>
        )}
      </div>
    </div>
  );
};

export default StatusWidget;
